import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import { useAuthStore } from '../stores/authStore'
import { fireFirstCardStudied } from '../utils/funnelEvents'
import type {
  LearningAnswerResult,
  LearningSessionCreateResponse,
  LearningSessionStatus,
  NextCardData,
  RetypeResult,
} from '../types'

// ── Create session ─────────────────────────────────────────────────────────────

interface CreateSessionParams {
  source_type: 'deck' | 'folder' | 'course' | 'custom'
  source_id?: string
  deck_ids?: string[]
  card_ids?: string[]
  prompt_side_label?: string
  is_prep?: boolean
}

export function useCreateLearningSession() {
  return useMutation<LearningSessionCreateResponse, Error, CreateSessionParams>({
    mutationFn: (body) =>
      api.post('/api/v1/learning/sessions', body).then((r) => r.data),
  })
}

// ── Session status ─────────────────────────────────────────────────────────────

export function useLearningSessionStatus(sessionId: string | undefined) {
  return useQuery<LearningSessionStatus>({
    queryKey: ['learning-session', sessionId],
    queryFn: () =>
      api.get(`/api/v1/learning/sessions/${sessionId}`).then((r) => r.data),
    enabled: !!sessionId,
    staleTime: 0,
  })
}

// ── Next card ─────────────────────────────────────────────────────────────────

export function useNextLearningCard(sessionId: string | undefined) {
  return useQuery<NextCardData | null>({
    queryKey: ['learning-next-card', sessionId],
    queryFn: () =>
      api
        .get(`/api/v1/learning/sessions/${sessionId}/next-card`)
        // Backend always returns JSON now (session_complete:true when done).
        // The 204 fallback is kept for safety against stale server deployments.
        .then((r) => (r.status === 204 ? null : (r.data as NextCardData))),
    enabled: !!sessionId,
    staleTime: 0,
    retry: (failCount, error: unknown) => {
      const status = (error as { response?: { status: number } })?.response?.status
      // 404: gone. 409: a definitive refusal (not-active, or ADR-082
      // SESSION_DAY_ENDED) — retrying re-asks a question the server has
      // already answered.
      if (status === 404 || status === 409) return false
      return failCount < 2
    },
  })
}

// ── Submit answer ─────────────────────────────────────────────────────────────

interface AnswerParams {
  card_id: string
  user_answer: string
  response_time_ms?: number
  hint_used?: boolean
  is_multiple_choice?: boolean
  is_sentence_build?: boolean
}

export function useSubmitAnswer(sessionId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<LearningAnswerResult, Error, AnswerParams>({
    mutationFn: (body) =>
      api
        .post(`/api/v1/learning/sessions/${sessionId}/answer`, body)
        .then((r) => r.data),
    onSuccess: () => {
      // Refresh session stats; next-card is re-fetched by UI after delay
      qc.invalidateQueries({ queryKey: ['learning-session', sessionId] })
      // Fire first_card_studied exactly once per user lifetime (fire-and-forget)
      const { user, setUser } = useAuthStore.getState()
      if (user) {
        fireFirstCardStudied(user, setUser).catch(() => {})
      }
    },
  })
}

// ── Retype ────────────────────────────────────────────────────────────────────

interface RetypeParams {
  card_id: string
  user_answer: string
}

export function useRetype(sessionId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<RetypeResult, Error, RetypeParams>({
    mutationFn: (body) =>
      api
        .post(`/api/v1/learning/sessions/${sessionId}/retype`, body)
        .then((r) => r.data),
    onSuccess: (data) => {
      if (data.accepted) {
        qc.invalidateQueries({ queryKey: ['learning-session', sessionId] })
      }
    },
  })
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

export function usePauseLearningSession() {
  const qc = useQueryClient()
  return useMutation<{ id: string; status: string }, Error, string>({
    mutationFn: (sessionId) =>
      api.patch(`/api/v1/learning/sessions/${sessionId}/pause`).then((r) => r.data),
    onSuccess: (_, sessionId) => {
      qc.invalidateQueries({ queryKey: ['learning-session', sessionId] })
    },
  })
}

export function useResumeSession() {
  const qc = useQueryClient()
  return useMutation<{ id: string; status: string }, Error, string>({
    mutationFn: (sessionId) =>
      api.patch(`/api/v1/learning/sessions/${sessionId}/resume`).then((r) => r.data),
    onSuccess: (_, sessionId) => {
      qc.invalidateQueries({ queryKey: ['learning-session', sessionId] })
    },
  })
}

// ── Manual reset (ADR-098) ──────────────────────────────────────────────────────
// The learner's statement that they have forgotten. Nothing calls these except an
// explicit, confirmed action.

export interface DeckResetPreview {
  deck_id: string
  card_count: number
}

function invalidateAfterReset(qc: ReturnType<typeof useQueryClient>, deckId: string) {
  qc.invalidateQueries({ queryKey: ['cards', deckId] })
  qc.invalidateQueries({ queryKey: ['deck', deckId] })
  qc.invalidateQueries({ queryKey: ['decks'] })
  qc.invalidateQueries({ queryKey: ['deck-reset-preview', deckId] })
  qc.invalidateQueries({ queryKey: ['progress-summary'] })
  qc.invalidateQueries({ queryKey: ['vocabulary-map'] })
  qc.invalidateQueries({ queryKey: ['difficult-words'] })
  qc.invalidateQueries({ queryKey: ['smart-queue'] })
  qc.invalidateQueries({ queryKey: ['queue-composition'] })
}

export function useResetCardMastery(deckId: string) {
  const qc = useQueryClient()
  return useMutation<{ card_id: string; mastery_level: number | null; is_mastered: boolean; was_reset: boolean }, Error, string>({
    mutationFn: (cardId) =>
      api.post(`/api/v1/learning/cards/${cardId}/reset-mastery`).then((r) => r.data),
    onSuccess: () => invalidateAfterReset(qc, deckId),
  })
}

/** The count a deck reset would apply to, read fresh each time the confirm opens. */
export function useDeckResetPreview(deckId: string, enabled: boolean) {
  return useQuery<DeckResetPreview>({
    queryKey: ['deck-reset-preview', deckId],
    queryFn: () => api.get(`/api/v1/learning/decks/${deckId}/reset-mastery`).then((r) => r.data),
    enabled,
    staleTime: 0,
    gcTime: 0,
  })
}

export function useResetDeckMastery(deckId: string) {
  const qc = useQueryClient()
  return useMutation<{ deck_id: string; reset_count: number }, Error, number>({
    // expected_count: the server refuses (409) if the count moved since it was shown.
    mutationFn: (expectedCount) =>
      api.post(`/api/v1/learning/decks/${deckId}/reset-mastery`, { expected_count: expectedCount }).then((r) => r.data),
    onSuccess: () => invalidateAfterReset(qc, deckId),
  })
}
